// theme-system/SchemaLoader.js
// Loads UI schema from window.__INITIAL_THEME_CONFIG__ (Razor / HomeController) and provides it via context

import React, { useState, useEffect, Suspense } from 'react';
import LoadingPlaceholder from '../components/blocks/LoadingPlaceholder';
import { SchemaContext } from './SchemaContext';
import LayoutEngine from './LayoutEngine';

/** @param {Record<string, unknown>|string|null|undefined} config - Theme layout JSON, or JSON string */
function applyUserThemeConfig(config, hostname, applySchema, logSource) {
  if (config == null || config === '') {
    //console.log(`[SchemaLoader] No theme config in ${logSource}, schema will be null`);
    applySchema(null, null, hostname);
    return;
  }

  let schema;
  try {
    schema = typeof config === 'string' ? JSON.parse(config) : config;
  } catch (e) {
    console.warn('[SchemaLoader] Invalid schema JSON:', e, '- schema will be null');
    applySchema(null, null, hostname);
    return;
  }

  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) {
    console.warn(`[SchemaLoader] Theme config must be a schema object (${logSource}), schema will be null`);
    applySchema(null, null, hostname);
    return;
  }

  applySchema(schema, null, hostname);
  //console.log(`[SchemaLoader] Schema loaded (${logSource}):`, schema);
}

const SchemaLoader = ({ children }) => {
  const [contextValue, setContextValue] = useState({
    vendorId: null,
    hostname: window.location.hostname,
    schema: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const loadSchema = () => {
      const hostname = window.location.hostname;

      const applySchema = (schema, vendorId = null, configHostname = hostname) => {
        setContextValue({
          vendorId,
          hostname: configHostname,
          schema,
          isLoading: false,
          error: null,
        });
      };

      try {
        const boot = typeof window !== 'undefined' ? window.__INITIAL_THEME_CONFIG__ : undefined;
        if (boot != null && boot !== '' && (typeof boot === 'object' || typeof boot === 'string')) {
          //console.log('[SchemaLoader] Using server-injected theme (Home/Index)');
          applyUserThemeConfig(boot, hostname, applySchema, 'server bootstrap');
          return;
        }

        console.warn('[SchemaLoader] No window.__INITIAL_THEME_CONFIG__; schema will be null');
        applySchema(null);
      } catch (err) {
        console.error('[SchemaLoader] Error loading schema:', err);
        applySchema(null);
      }
    };

    loadSchema();
  }, []);

  // Show error state
  if (contextValue.error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <h3>Error Loading Theme</h3>
        <p>{contextValue.error.message}</p>
      </div>
    );
  }

  // Show loading state
  if (contextValue.isLoading) {
    return <LoadingPlaceholder />;
  }

  return (
    <SchemaContext.Provider value={contextValue}>
      <Suspense fallback={<LoadingPlaceholder />}>
        <LayoutEngine>
          {children}
        </LayoutEngine>
      </Suspense>
    </SchemaContext.Provider>
  );
};

export default SchemaLoader;
